import { Action } from '@reduxjs/toolkit';

import { logout } from 'app/redux/actions/auth';

import {
  ADD_BIG_STAKE, AddBigStake,
  CLOSE_BIG_STAKE_BAR, CloseBigStakeBar,
  FetchBigStakesSuccess,
  fetchPendingBigStakes, HIDE_BIG_STAKE_INFO, HideBigStakeInfo,
  SHOW_BIG_STAKE_INFO, ShowBigStakeInfo,
} from '../actions/bigStakes';
import {
  SUBMIT_STAKE_FAILURE, SUBMIT_STAKE_SUCCESS, SubmitStakeFailure,
  SubmitStakeSuccess,
} from '../actions/submission';
import { SubmissionState, TypeOfError } from '../constants';
import { BigStake, EntryError } from '../records';
import { BigStakesState } from '../types/bigStakes';

// ---

const initialState: BigStakesState = {
  stakes: new Map(),
  errors: new Map(),
  visibleBigStakeInfo: false,
};

type Logout = Action<typeof logout>;

export type BigStakesActions =
  | AddBigStake
  | SubmitStakeSuccess
  | SubmitStakeFailure
  | ShowBigStakeInfo
  | HideBigStakeInfo
  | CloseBigStakeBar
  | Logout;

// ---

function addBigStake(
  state: BigStakesState,
  payload: AddBigStake['payload'],
): BigStakesState {
  if (!payload.stake) {
    console.error('addBigStake -> `stake` not found');
    return state;
  }

  return {
    ...state,
    stakes: new Map(state.stakes).set(
      payload.stake.dialogId,
      BigStake(payload.stake).set('submission', SubmissionState.PROCESSING),
    ),
  };
}

function addPendingBigStakes(
  state: BigStakesState,
  payload: FetchBigStakesSuccess['payload'],
): BigStakesState {
  const stakes = new Map(state.stakes);

  payload.data.forEach((stake) => {
    stakes.set(stake.dialogId, BigStake(stake).set('submission', SubmissionState.PROCESSING));
  });

  return { ...state, stakes };
}

// ---

function handleSubmitStakeSuccess(
  state: BigStakesState,
  payload: SubmitStakeSuccess['payload'],
): BigStakesState {
  const stakes = new Map(state.stakes);
  const stake = stakes.get(payload.dialogId);

  if (!stake) {
    return state;
  }

  stakes.set(payload.dialogId, stake.set('submission', SubmissionState.ACCEPTED));

  return { ...state, stakes };
}

function handleSubmitStakeFailure(
  state: BigStakesState,
  error: SubmitStakeFailure['error'],
  payload: SubmitStakeFailure['payload'],
): BigStakesState {
  const errors = new Map(state.errors);
  const stakes = new Map(state.stakes);
  const stake = stakes.get(payload.dialogId);

  if (!stake) {
    return state;
  }
  const bigStakeEntryError = EntryError({
    code: error.status,
    type: TypeOfError.GENERAL,
    rejectReason: error.rejectReason,
  });

  stakes.set(payload.dialogId, stake.set('submission', SubmissionState.FAILED));
  errors.set(payload.dialogId, bigStakeEntryError);

  return { ...state, stakes, errors };
}

// ---

export default function (
  state = initialState,
  action: BigStakesActions,
): BigStakesState {
  switch (action.type) {
    case ADD_BIG_STAKE:
      return addBigStake(state, action.payload);

    case fetchPendingBigStakes.fulfilled.toString():
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore TODO: должно уйти, когда этот редьюсер будет переписан на slice
      const payloadWithType = action.payload as unknown as FetchBigStakesSuccess['payload'];
      return addPendingBigStakes(state, payloadWithType);

    case SUBMIT_STAKE_SUCCESS:
      return handleSubmitStakeSuccess(state, action.payload);

    case SUBMIT_STAKE_FAILURE:
      return handleSubmitStakeFailure(state, action.error, action.payload);

    case SHOW_BIG_STAKE_INFO:
      return {
        ...state,
        visibleBigStakeInfo: true,
      };

    case HIDE_BIG_STAKE_INFO:
      return {
        ...state,
        visibleBigStakeInfo: false,
      };

    case CLOSE_BIG_STAKE_BAR:
    case logout.pending.type: {
      return initialState;
    }

    default:
      return state;
  }
}
